import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import * as Constants from '../../../../config/Constants'
import getText from '../../../shared/i18n/labels'

import * as actions from '../../../../actions'

// TABLE
import { baseSearchObject, fieldTypes, TableUtils } from '../../../shared/tables/TableUtils'
import { StarTable } from '../../../shared/tables/StarTable'

// FORM
import { CrsForm } from './NewCrsSubjectForm'

// COMPONENTS
import { PopupSubjectLicense } from '../../../shared/popups/PopupSubjectLicense'
import { LicenseExpiredPopup } from '../../../shared/auth/license/LicenseExpiredPopup'
import { formatDateForDisplay } from '../../../shared/Utility'

const getTableContainerWidth = () => {
  if (window.innerWidth < Constants.BREAKPOINT_MD) return '130%'
  return '100%'
}

export function CrsSubjectList(props) {
  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const [show] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  const [searchObject, setSearchObject] = useState({
    ...baseSearchObject,
    subjectCode: null,
    fiscalCode: null,
    companyName: null,
    idFiscalOicrTrust: null,
    referenceYear: null,
    id: null
  })

  const locations = useSelector((state) =>
    state.locations?.data.map((location, index) => ({
      name: location.location,
      key: index
    }))
  )

  useEffect(() => {
    const subjectSummary = props.subjectSummary
    if (subjectSummary?.available < 0) {
      PopupSubjectLicense(
        {
          total: subjectSummary?.total,
          used: subjectSummary?.used,
          available: subjectSummary?.available,
          name: Constants.APPLICATION_CRS
        },
        lang
      )
    }
  }, [props.subjectSummary])

  useEffect(() => {
    props.setSearchObject(searchObject)
  }, [searchObject])

  const countries = useSelector((state) =>
    state.countries?.data.map((country) => ({
      name: country.code,
      key: country.description
    }))
  )

  const [id, setId] = useState(null)
  const tableUtils = TableUtils
  const tableName = 'crs-subject'
  const history = useHistory()

  const header = [
    tableUtils.composeHeader({
      fieldName: 'subjectCode',
      displayedName: labels.CODE,
      maxWidth: '1fr'
    }),
    tableUtils.composeHeader({
      fieldName: 'fiscalCode',
      displayedName: labels.FISCAL_CODE,
      maxWidth: '1.5fr'
    }),
    tableUtils.composeHeader({
      fieldName: 'companyName',
      displayedName: labels.OBLIGATED_SUBJECT,
      maxWidth: '4fr'
    }),
    tableUtils.composeHeader({
      fieldName: 'idFiscalOicrTrust',
      displayedName: 'OICR',
      maxWidth: '1.2fr'
    }),
    tableUtils.composeHeader({
      fieldName: 'referenceYear',
      displayedName: labels.REFERENCE_YEAR,
      maxWidth: '1.8fr'
    }),
    tableUtils.composeHeader({
      fieldName: 'expiration',
      type: fieldTypes.DATERANGE,
      displayedName: labels.SID_EXPIRATION,
      maxWidth: '1.5fr'
    })
  ]

  const prepareRows = (newSubjects) => {
    return newSubjects.map((c) => {
      return tableUtils.composeRow({
        id: c.id,
        cellData: {
          subjectCode: tableUtils.composeCell({
            fieldName: 'subjectCode',
            fieldValue: c.subjectCode
          }),
          fiscalCode: tableUtils.composeCell({
            fieldName: 'fiscalCode',
            fieldValue: c.fiscalCode
          }),
          companyName: tableUtils.composeCell({
            fieldName: 'companyName',
            fieldValue: c.companyName
          }),
          idFiscalOicrTrust: tableUtils.composeCell({
            fieldName: 'idFiscalOicrTrust',
            fieldValue: c.idFiscalOicrTrust
          }),
          referenceYear: tableUtils.composeCell({
            fieldName: 'referenceYear',
            fieldValue: c.referenceYear
          }),
          expiration: tableUtils.composeCell({
            fieldName: 'expiration',
            fieldValue: formatDateForDisplay(c.expiration)
          })
        }
      })
    })
  }

  const onClickRow = (subject) => {
    setId(subject.id)
  }

  const onDoubleClickRow = (subject) => {
    if (props.subjectSummary?.available < 0) {
      PopupSubjectLicense(
        {
          total: props.subjectSummary?.total,
          used: props.subjectSummary?.used,
          available: props.subjectSummary?.available,
          name: Constants.APPLICATION_CRS
        },
        lang
      )
      return
    }
    if (props.disabled) {
      LicenseExpiredPopup({})
      return
    }
    setId(subject.id)
    const path = props.licenseExpired ? '#' : `/app/crs/dashboard/relationship/${subject.id}`
    history.push(path)
  }

  const setNewSubjects = (subjects) => {
    props.setData(subjects, searchObject)
  }

  const searchCrsSubjects = (_searchObject) => {
    setLoading(true)
    return actions.searchCrsSubjects(_searchObject).finally(() => setLoading(false))
  }

  useEffect(() => {
    setRows(prepareRows(props.subjects))
    setId(id ? id : props.subjects?.[0]?.id)
  }, [props.subjects])

  useEffect(() => {
    setId()
  }, [props.resetForm])

  const renderTable = () => {
    return (
      <StarTable
        id={id}
        version="1"
        application="CRS"
        routePath={'/app/crs/dashboard'}
        hideSearch={props.hideSearch}
        headerColumns={header}
        checkedItems={props.checkedItems}
        rows={rows}
        loading={show}
        tableContainerWidth={getTableContainerWidth()}
        tableConfigurationKey={tableName}
        searchObjectPrototype={searchObject}
        setSearchObject={setSearchObject}
        searchCallBack={(_searchObject) => {
          setId()
          return searchCrsSubjects(_searchObject)
        }}
        onHideSearch={(val) => props.setHideSearch(val)}
        onExecutedSearch={(content) => setNewSubjects(content)}
        usePagination={true}
        useHover={props.useHover}
        withCheckBoxes={true}
        onClickRow={onClickRow}
        onDoubleClickRow={onDoubleClickRow}
        onClickCheck={props.onClickCheck}
        actionsComponent={props.actionsComponent}
        formComponent={CrsForm}
        formComponentProps={{
          disabled: props.disabled || loading || (props.disableNewSubjectButtons && !id),
          formOnBottom: true,
          subjectId: id,
          locations: locations,
          countries: countries,
          setLoading: setLoading,
          onSaveClick: (subject, reload) => {
            // Da usare solo al click del pulsante salva
            props.setUseHover(true)
            props.getSubjects()
            setId(subject.id)
          },
          onOtherSaveAction: (subject) => {
            // Da usare quando non si clicca sul pulsante salva
            props.getSubjects()
            setId(subject.id)
          }
        }}
      />
    )
  }

  return <>{renderTable()}</>
}

export default CrsSubjectList
